import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Plus, TrendingUp, DollarSign, Percent, Target, Calendar, Mail, Phone, Building, Edit, Trash2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusConfig = {
  'Prospect': { color: 'bg-gray-100 text-gray-800', icon: Target },
  'Qualifié': { color: 'bg-blue-100 text-blue-800', icon: TrendingUp },
  'Proposition': { color: 'bg-purple-100 text-purple-800', icon: TrendingUp },
  'Négociation': { color: 'bg-orange-100 text-orange-800', icon: TrendingUp },
  'Gagné': { color: 'bg-green-100 text-green-800', icon: TrendingUp },
  'Perdu': { color: 'bg-red-100 text-red-800', icon: TrendingUp }
};

export const SalesCRMPage = ({ getAuthHeaders }) => {
  const [sales, setSales] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);
  const [formData, setFormData] = useState({
    prospect_name: '',
    company: '',
    email: '',
    phone: '',
    service_interested: '',
    estimated_amount: '',
    description: ''
  });

  const loadSales = async () => {
    try {
      const [salesRes, statsRes] = await Promise.all([
        axios.get(`${API}/sales`, getAuthHeaders()),
        axios.get(`${API}/sales/stats`, getAuthHeaders())
      ]);
      setSales(salesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error loading sales:', error);
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSales();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/sales`, {
        ...formData,
        estimated_amount: parseFloat(formData.estimated_amount) || 0
      }, getAuthHeaders());
      toast.success("Opportunité créée");
      setShowDialog(false);
      resetForm();
      loadSales();
    } catch (error) {
      console.error('Error creating sale:', error);
      toast.error("Erreur lors de la création");
    }
  };

  const handleStatusChange = async (saleId, newStatus) => {
    try {
      await axios.patch(`${API}/sales/${saleId}`, { status: newStatus }, getAuthHeaders());
      toast.success("Statut mis à jour");
      loadSales();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleSetFollowup = async (saleId, date) => {
    try {
      await axios.patch(`${API}/sales/${saleId}`, { next_followup: date }, getAuthHeaders());
      toast.success("Relance programmée");
      loadSales();
    } catch (error) {
      console.error('Error setting followup:', error);
      toast.error("Erreur");
    }
  };

  const handleDelete = async (saleId) => {
    if (!window.confirm('Supprimer cette opportunité?')) return;
    
    try {
      await axios.delete(`${API}/sales/${saleId}`, getAuthHeaders());
      toast.success("Opportunité supprimée");
      loadSales();
      setShowDetailDialog(false);
    } catch (error) {
      console.error('Error deleting sale:', error);
      toast.error("Erreur");
    }
  };

  const resetForm = () => {
    setFormData({
      prospect_name: '',
      company: '',
      email: '',
      phone: '',
      service_interested: '',
      estimated_amount: '',
      description: ''
    });
  };

  const groupedSales = {
    'Prospect': sales.filter(s => s.status === 'Prospect'),
    'Qualifié': sales.filter(s => s.status === 'Qualifié'),
    'Proposition': sales.filter(s => s.status === 'Proposition'),
    'Négociation': sales.filter(s => s.status === 'Négociation'),
    'Gagné': sales.filter(s => s.status === 'Gagné'),
    'Perdu': sales.filter(s => s.status === 'Perdu')
  };

  if (loading) {
    return <div className="flex items-center justify-center p-12"><div className="text-gray-600">Chargement...</div></div>;
  }

  return (
    <div>
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Target className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">{stats.total_opportunities}</div>
                  <div className="text-sm text-gray-600">Opportunités</div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">{stats.total_value.toLocaleString('fr-CA', {style: 'currency', currency: 'CAD'})}</div>
                  <div className="text-sm text-gray-600">Valeur totale</div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">{stats.won_amount.toLocaleString('fr-CA', {style: 'currency', currency: 'CAD'})}</div>
                  <div className="text-sm text-gray-600">CA réalisé</div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Percent className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">{stats.conversion_rate}%</div>
                  <div className="text-sm text-gray-600">Taux conversion</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Pipeline de Ventes</h2>
        <Button
          onClick={() => setShowDialog(true)}
          className="bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nouvelle Opportunité
        </Button>
      </div>

      {/* Kanban Board */}
      <div className="grid lg:grid-cols-3 xl:grid-cols-6 gap-4 overflow-x-auto pb-4">
        {Object.entries(groupedSales).map(([status, opportunities]) => (
          <div key={status} className="min-w-[280px]">
            <div className="bg-gray-50 rounded-lg p-3 mb-3">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-gray-900">{status}</h3>
                <Badge className={statusConfig[status].color}>
                  {opportunities.length}
                </Badge>
              </div>
            </div>

            <div className="space-y-3">
              {opportunities.map((sale) => (
                <Card
                  key={sale.id}
                  className="hover:shadow-lg transition-all cursor-pointer"
                  onClick={() => {
                    setSelectedSale(sale);
                    setShowDetailDialog(true);
                  }}
                >
                  <CardContent className="p-4">
                    <div className="space-y-2">
                      <div className="font-semibold text-gray-900">{sale.prospect_name}</div>
                      <div className="text-sm text-gray-600">{sale.company}</div>
                      <div className="flex items-center justify-between">
                        <span className="text-lg font-bold text-green-600">
                          {(sale.estimated_amount || 0).toLocaleString('fr-CA', {style: 'currency', currency: 'CAD'})}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500">{sale.service_interested}</div>
                      {sale.next_followup && (
                        <div className="flex items-center gap-1 text-xs text-orange-600">
                          <Calendar className="w-3 h-3" />
                          Relance: {new Date(sale.next_followup).toLocaleDateString('fr-CA')}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Create Dialog - continued in next part */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nouvelle Opportunité de Vente</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="prospect_name">Nom du prospect *</Label>
                <Input
                  id="prospect_name"
                  value={formData.prospect_name}
                  onChange={(e) => setFormData({...formData, prospect_name: e.target.value})}
                  required
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="company">Entreprise *</Label>
                <Input
                  id="company"
                  value={formData.company}
                  onChange={(e) => setFormData({...formData, company: e.target.value})}
                  required
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="phone">Téléphone *</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  required
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="service">Service intéressé *</Label>
                <Select
                  value={formData.service_interested}
                  onValueChange={(value) => setFormData({...formData, service_interested: value})}
                  required
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Sélectionnez" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Support Informatique">Support Informatique</SelectItem>
                    <SelectItem value="Vente & Location">Vente & Location</SelectItem>
                    <SelectItem value="Modems Événements">Modems Événements</SelectItem>
                    <SelectItem value="Terminaux POS">Terminaux POS</SelectItem>
                    <SelectItem value="Logiciels Sur Mesure">Logiciels Sur Mesure</SelectItem>
                    <SelectItem value="Consulting IT">Consulting IT</SelectItem>
                    <SelectItem value="Sécurité">Sécurité & Surveillance</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="amount">Montant estimé (CAD)</Label>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  value={formData.estimated_amount}
                  onChange={(e) => setFormData({...formData, estimated_amount: e.target.value})}
                  className="mt-2"
                  placeholder="0.00"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="description">Description / Besoins *</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                required
                className="mt-2 min-h-[100px]"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="submit" className="flex-1 bg-green-600 hover:bg-green-700">
                Créer l'opportunité
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowDialog(false);
                  resetForm();
                }}
              >
                Annuler
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog - continued in component */}
      {selectedSale && (
        <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                Opportunité: {selectedSale.prospect_name}
                <Badge className={statusConfig[selectedSale.status].color}>
                  {selectedSale.status}
                </Badge>
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm text-gray-600">Entreprise</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Building className="w-4 h-4 text-gray-400" />
                    <span className="font-medium">{selectedSale.company}</span>
                  </div>
                </div>
                <div>
                  <Label className="text-sm text-gray-600">Montant estimé</Label>
                  <div className="text-2xl font-bold text-green-600 mt-1">
                    {(selectedSale.estimated_amount || 0).toLocaleString('fr-CA', {style: 'currency', currency: 'CAD'})}
                  </div>
                </div>
                <div>
                  <Label className="text-sm text-gray-600">Email</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <a href={`mailto:${selectedSale.email}`} className="text-blue-600 hover:underline">
                      {selectedSale.email}
                    </a>
                  </div>
                </div>
                <div>
                  <Label className="text-sm text-gray-600">Téléphone</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Phone className="w-4 h-4 text-gray-400" />
                    <a href={`tel:${selectedSale.phone}`} className="text-blue-600 hover:underline">
                      {selectedSale.phone}
                    </a>
                  </div>
                </div>
              </div>

              <div>
                <Label className="text-sm text-gray-600">Service intéressé</Label>
                <p className="mt-1 font-medium">{selectedSale.service_interested}</p>
              </div>

              <div>
                <Label className="text-sm text-gray-600">Description</Label>
                <p className="mt-1 bg-gray-50 p-3 rounded-lg text-sm">{selectedSale.description}</p>
              </div>

              {selectedSale.notes && (
                <div>
                  <Label className="text-sm text-gray-600">Notes</Label>
                  <p className="mt-1 bg-yellow-50 p-3 rounded-lg text-sm border border-yellow-200">{selectedSale.notes}</p>
                </div>
              )}

              <div>
                <Label className="text-sm text-gray-600 mb-2 block">Changer le statut</Label>
                <div className="flex flex-wrap gap-2">
                  {Object.keys(statusConfig).map((status) => (
                    <Button
                      key={status}
                      size="sm"
                      variant={selectedSale.status === status ? 'default' : 'outline'}
                      onClick={() => handleStatusChange(selectedSale.id, status)}
                      className={selectedSale.status === status ? statusConfig[status].color : ''}
                    >
                      {status}
                    </Button>
                  ))}
                </div>
              </div>

              <div>
                <Label htmlFor="followup" className="text-sm text-gray-600">Programmer une relance</Label>
                <Input
                  id="followup"
                  type="date"
                  className="mt-2"
                  onChange={(e) => handleSetFollowup(selectedSale.id, e.target.value)}
                />
              </div>

              <div className="flex gap-2 pt-4 border-t">
                <Button
                  variant="outline"
                  className="text-red-600 hover:bg-red-50"
                  onClick={() => handleDelete(selectedSale.id)}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Supprimer
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};
